import {
  CustomButton,
  DOCUMENT_SECURITY_STATUS,
  FormInput,
  SvgCross,
  MULTI_UPLOAD_FILE_TYPE,
  SelectDropDown,
} from '@ui-kit'
import React, { useEffect, useMemo, useState } from 'react'
import { QUEUE_STRINGS } from '../../utils/queueIntake.types'
import { BUTTONS, NAMESPACES, EXTENSIONS } from '../../utils/constants'
import { Controller, useForm } from 'react-hook-form'
import {
  SplitFaxProps,
  SplitFormData,
  SplitObject,
} from '../../types/intakeQueue.types'
import { SplitType } from '../../utils/types'
import { convertS3ToBase64, DropdownType } from '@app-store'
import FilePreview from './FilePreview'
import { RootState, useAppSelector } from '../../utils/agent.store'
import { getFileType } from '../../utils/helperFunctions'

const SplitFax: React.FC<SplitFaxProps> = ({
  enableIdentification = false,
  showFaxSplitPanel = true,
  faxDetails,
  setIsSplitFax,
  faxUrl,
  splitFaxFunction,
  splitFaxFunctionWithIdentification,
  selectedFile,
  fromQueueTable,
  defaultFileType,
}) => {
  console.log(faxDetails, selectedFile)
  const [isFaxSplitPanelVisible, setFaxSplitPanelVisible] =
    useState(showFaxSplitPanel)
  const documentTypeOptions = useAppSelector(
    (state: RootState) =>
      state.categoryDropdown.categoryMap['Case Document Type']
  )
  const {
    register: splitRegister,
    handleSubmit: handleSplitSubmit,
    formState: { errors: splitErrors, isValid },
    setValue: setSplitValue,
    control,
    trigger,
    clearErrors,
  } = useForm<SplitFormData>({
    mode: 'onChange',
    defaultValues: {
      splitFaxArray: [{ splits: '', name: '', document_type: undefined }],
    },
  })
  const [splitsType, setSplitsType] = useState<SplitType>(
    SplitType.CUSTOM_SPLIT
  )
  const [faxFields, setFaxFields] = useState<
    { splits: string; name: string; document_type?: number }[]
  >([{ splits: '', name: '', document_type: undefined }])
  const [totalPages, setTotalPages] = useState(faxDetails?.total_pages ?? 0)
  const [docxPreviewUrl, setDocxPreviewUrl] = useState<string>('')
  const fileType = useMemo(
    () => defaultFileType || getFileType(selectedFile?.content_type, faxUrl),
    [selectedFile?.content_type, faxUrl, defaultFileType]
  )
  const isDocumentReady =
    selectedFile?.status === undefined ||
    selectedFile.status === DOCUMENT_SECURITY_STATUS.READY
  const isSplitDisabled = fileType !== EXTENSIONS.PDF || !isDocumentReady

  const addFaxField = () => {
    const updatedFaxFields = [
      ...faxFields,
      { splits: '', name: '', document_type: undefined },
    ]
    setFaxFields(updatedFaxFields)
    setSplitValue('splitFaxArray', updatedFaxFields)
  }

  const removeFaxField = (index: number) => {
    const updatedFaxFields = faxFields.filter((_, i) => i !== index)
    clearErrors(`splitFaxArray.${index}`)
    setFaxFields(updatedFaxFields)
    setSplitValue('splitFaxArray', updatedFaxFields)
    setTimeout(() => {
      trigger()
    }, 0)
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.code === 'Space') event.preventDefault()
  }

  const onSubmit = (data: SplitFormData): void => {
    const apiData = data?.splitFaxArray?.map((item) => {
      const splits =
        splitsType === SplitType.RANGE_SPLIT
          ? range(...(item.splits.split('-').map(Number) as [number, number]))
          : item.splits.split(',').map(Number)
      return enableIdentification
        ? {
            pageRanges: splits,
            documentName: item.name,
            documentTypeId: item.document_type,
          }
        : splits
    }) as number[][] | SplitObject[]

    if (Array.isArray(apiData[0])) {
      splitFaxFunction && splitFaxFunction(apiData as number[][])
    } else {
      splitFaxFunctionWithIdentification &&
        splitFaxFunctionWithIdentification(apiData as SplitObject[])
    }
  }

  const validateInput = (value: string) => {
    const regex = /^([1-9]\d{0,4})(,([1-9]\d{0,4}))*$/

    if (!regex.test(value)) {
      return NAMESPACES.INPUT_CANT_END_WITH_COMMA
    }

    const values = value.split(',')
    const invalidPages: number[] = []

    for (const num of values) {
      const numValue = parseInt(num, 10)

      if (numValue === 0) {
        return NAMESPACES.VALUES_CANNOT_BE_ZERO
      }

      if (numValue > totalPages) {
        invalidPages.push(numValue)
      }
    }

    if (invalidPages.length > 0) {
      const pageLabel =
        invalidPages.length === 1
          ? NAMESPACES.ERROR_PAGE_NUMBER_SINGULAR
          : NAMESPACES.ERROR_PAGE_NUMBER_PLURAL
      const validityMessage =
        invalidPages.length === 1
          ? NAMESPACES.ERROR_IS_INVALID
          : NAMESPACES.ERROR_ARE_INVALID
      return `${pageLabel} ${invalidPages.join(', ')} ${validityMessage} ${
        NAMESPACES.ERROR_PLEASE_ENTER_VALID_VALUES
      } ${totalPages}.`
    }

    return true
  }

  const validateInputRange = (value: string) => {
    const regex = /^([1-9]\d{0,4})(-([1-9]\d{0,4}))*$/

    if (!regex.test(value)) {
      return NAMESPACES.INPUT_CANT_END_WITH_COMMA
    }

    const values = value.split('-')
    const invalidPages: number[] = []

    if (values.length > 2) return NAMESPACES.ENTER_VALID_RANGE
    for (const num of values) {
      const numValue = parseInt(num, 10)

      if (numValue === 0) {
        return NAMESPACES.VALUES_CANNOT_BE_ZERO
      }

      if (numValue > totalPages) {
        invalidPages.push(numValue)
      }
    }

    if (invalidPages.length > 0) {
      const pageLabel =
        invalidPages.length === 1
          ? NAMESPACES.ERROR_PAGE_NUMBER_SINGULAR
          : NAMESPACES.ERROR_PAGE_NUMBER_PLURAL
      const validityMessage =
        invalidPages.length === 1
          ? NAMESPACES.ERROR_IS_INVALID
          : NAMESPACES.ERROR_ARE_INVALID
      return `${pageLabel} ${invalidPages.join(', ')} ${validityMessage} ${
        NAMESPACES.ERROR_PLEASE_ENTER_VALID_VALUES
      } ${totalPages}.`
    }

    return true
  }

  function range(start: number, end: number) {
    const result = []
    for (let i = start; i <= end; i++) {
      result.push(i)
    }
    return result
  }

  const handleSplitsTypeToggle = (splitType: SplitType) => {
    const updatedFaxFields = faxFields.map((fields) => {
      return {
        splits: '',
        name: fields.name,
      }
    })
    setFaxFields(updatedFaxFields)
    setSplitValue('splitFaxArray', updatedFaxFields)
    setSplitsType(splitType)
  }

  useEffect(() => {
    setSplitValue('splitFaxArray', faxFields)
  }, [faxFields, setSplitValue])

  useEffect(() => {
    const fetchBase64Data = async () => {
      if (
        selectedFile?.path &&
        (fileType === EXTENSIONS.DOCX || fileType === EXTENSIONS.DOC)
      ) {
        try {
          const response = await convertS3ToBase64(selectedFile.path)

          const base64Data = response.data
          const byteCharacters = atob(base64Data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], {
            type:
              fileType === EXTENSIONS.DOCX
                ? MULTI_UPLOAD_FILE_TYPE.DOCX
                : MULTI_UPLOAD_FILE_TYPE.DOC,
          })
          const blobUrl = URL.createObjectURL(blob)
          setDocxPreviewUrl(blobUrl)
        } catch (error) {
          console.error('Error fetching base64 data:', error)
        }
      }
    }

    fetchBase64Data()
    return () => {
      if (docxPreviewUrl) {
        URL.revokeObjectURL(docxPreviewUrl)
      }
    }
  }, [selectedFile?.path, fileType])

  return (
    <form className="flex" onSubmit={handleSplitSubmit(onSubmit)}>
      <div className="flex flex-col w-[64rem] h-[35.5rem]">
        {/* Header */}
        <div className="flex justify-between h-11 px-2 py-3">
          <span className="font-bold text-base leading-5">
            {isFaxSplitPanelVisible
              ? faxDetails?.category_communication_type?.label ===
                QUEUE_STRINGS.MAIL
                ? QUEUE_STRINGS.SPLIT_MAIL
                : QUEUE_STRINGS.SPLIT_FAX
              : QUEUE_STRINGS.DOCUMENT_PREVIEW}
          </span>
          {!isFaxSplitPanelVisible && !fromQueueTable && (
            <CustomButton
              height="20px"
              isFilled
              onClick={() => setFaxSplitPanelVisible(true)}
              disabled={isSplitDisabled}
              key={selectedFile?.url}
              className="bg-[#F6426F] text-white"
            >
              {NAMESPACES.SPLIT}
            </CustomButton>
          )}
          {!enableIdentification && (
            <button
              className="flex items-center justify-center h-4 w-4 cursor-pointer"
              onClick={() => setIsSplitFax(false)}
              type="button"
            >
              <SvgCross height={'0.875rem'} width={'0.875rem'} />
            </button>
          )}
        </div>

        <div className="flex border-t w-full h-full mb-2 split-fax-pdf">
          <div
            className={`${
              isFaxSplitPanelVisible ? 'w-[70.6%]' : 'w-[100%]'
            } border h-[100%] relative`}
          >
            <FilePreview
              url={faxUrl}
              fileType={fileType}
              setTotalPages={
                fileType === EXTENSIONS.PDF ? setTotalPages : undefined
              }
              docxPreviewUrl={docxPreviewUrl}
            />
          </div>
          {isFaxSplitPanelVisible && (
            <div className="max-w-[29.4%] w-full flex flex-col border h-full">
              <div className="h-[3.25rem] px-2 py-3 border">
                <span className="text-base leading-5 font-bold">
                  {faxDetails?.category_communication_type?.label ===
                  QUEUE_STRINGS.MAIL
                    ? QUEUE_STRINGS.SPLIT_MAIL_INTO
                    : QUEUE_STRINGS?.SPLIT_FAX_INTO}
                </span>
              </div>

              {enableIdentification && (
                <div className="flex justify-center pt-4">
                  <div className="relative flex bg-secondary-25 rounded-[2.27rem] gap-0.5">
                    <div
                      className={`absolute top-0.5 bottom-0.5 w-[50%] rounded-[2rem] bg-secondary-500 transition-all duration-300 ease-in-out ${
                        splitsType === SplitType.RANGE_SPLIT
                          ? 'left-1/2'
                          : 'left-0'
                      }`}
                    ></div>
                    <div
                      className={`w-[50%] relative z-10 transition-colors duration-300 text-xs py-2 px-8.5 rounded-[2.3rem] cursor-pointer ${
                        splitsType === SplitType.CUSTOM_SPLIT
                          ? 'text-white'
                          : 'text-border-50'
                      }`}
                      onClick={() =>
                        handleSplitsTypeToggle(SplitType.CUSTOM_SPLIT)
                      }
                    >
                      {SplitType.CUSTOM_SPLIT}
                    </div>
                    <div
                      className={`w-[50%] relative z-10 transition-colors duration-300 text-xs py-2 px-6 rounded-[2.3rem] cursor-pointer ${
                        splitsType === SplitType.RANGE_SPLIT
                          ? 'text-white'
                          : 'text-border-50'
                      }`}
                      onClick={() =>
                        handleSplitsTypeToggle(SplitType.RANGE_SPLIT)
                      }
                    >
                      {SplitType.RANGE_SPLIT}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col h-full overflow-y-auto">
                {faxFields.map((field, index) => (
                  <div
                    className="p-3 flex flex-col gap-3 border-b"
                    key={index + 'field'}
                  >
                    <div className="flex justify-between items-center">
                      <span>{`${
                        faxDetails?.category_communication_type?.label ===
                        QUEUE_STRINGS.MAIL
                          ? QUEUE_STRINGS.SPLIT_MAIL
                          : QUEUE_STRINGS.SPLIT_FAX
                      } ${index + 1}`}</span>
                      {index > 0 && (
                        <button
                          className="flex cursor-pointer"
                          onClick={() => removeFaxField(index)}
                          type="button"
                        >
                          <SvgCross />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-4">
                      {splitsType === SplitType.RANGE_SPLIT && (
                        <FormInput
                          name={`splitFaxArray.${index}.splits`}
                          register={splitRegister}
                          errors={splitErrors}
                          label={QUEUE_STRINGS?.RANGE}
                          placeholder={QUEUE_STRINGS?.EG_1_3}
                          validation={{
                            required: true,
                            validate: {
                              isPositive: (value) =>
                                validateInputRange(value as string) ||
                                NAMESPACES.ENTER_VALID_COMMA_SEPERATED_STRING,
                            },
                          }}
                          onChange={(e) => {
                            const updatedFaxFields = [...faxFields]
                            updatedFaxFields[index].splits = e.target.value
                            setFaxFields(updatedFaxFields)
                            setSplitValue('splitFaxArray', updatedFaxFields)
                            trigger(`splitFaxArray.${index}`)
                          }}
                        />
                      )}
                      {splitsType === SplitType.CUSTOM_SPLIT && (
                        <FormInput
                          name={`splitFaxArray.${index}.splits`}
                          register={splitRegister}
                          errors={splitErrors}
                          label={QUEUE_STRINGS?.PAGES}
                          placeholder={QUEUE_STRINGS?.EG_1_2_3}
                          validation={{
                            required: true,
                            validate: {
                              isPositive: (value) =>
                                validateInput(value as string) ||
                                NAMESPACES.ENTER_VALID_COMMA_SEPERATED_STRING,
                            },
                          }}
                          onKeyDown={onKeyDown}
                          onChange={(e) => {
                            const updatedFaxFields = [...faxFields]
                            updatedFaxFields[index].splits = e.target.value
                            setFaxFields(updatedFaxFields)
                            setSplitValue('splitFaxArray', updatedFaxFields)
                            trigger(`splitFaxArray.${index}.splits`)
                          }}
                        />
                      )}
                      {enableIdentification && (
                        <>
                          <FormInput
                            name={`splitFaxArray.${index}.name`}
                            register={splitRegister}
                            errors={splitErrors}
                            label={QUEUE_STRINGS?.DOCUMENT_NAME}
                            placeholder={QUEUE_STRINGS?.DOCUMENT_NAME}
                            validation={{
                              required: true,
                            }}
                            onChange={(e) => {
                              const updatedFaxFields = [...faxFields]
                              updatedFaxFields[index].name = e.target.value
                              setFaxFields(updatedFaxFields)
                              setTimeout(() => {
                                setSplitValue('splitFaxArray', updatedFaxFields)
                                trigger(`splitFaxArray.${index}.name`)
                              }, 0)
                            }}
                          />

                          <Controller
                            name={`splitFaxArray.${index}.document_type`}
                            control={control}
                            rules={{ required: 'Role is required' }}
                            render={({ field }) => (
                              <SelectDropDown
                                errors={splitErrors}
                                name={`splitFaxArray.${index}.document_type`}
                                label="Document Type"
                                options={
                                  documentTypeOptions as DropdownType<
                                    string | number,
                                    string
                                  >[]
                                }
                                onChange={(option) => {
                                  const updatedFaxFields = [...faxFields]
                                  updatedFaxFields[index].document_type =
                                    typeof option.id === 'number'
                                      ? option.id
                                      : undefined
                                  setFaxFields(updatedFaxFields)
                                  setTimeout(() => {
                                    setSplitValue(
                                      'splitFaxArray',
                                      updatedFaxFields
                                    )
                                    field.onChange(option.id)
                                  })
                                }}
                              />
                            )}
                          />
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  className="text-primary-500 flex w-full items-center justify-center h-6 mt-3 cursor-pointer mb-3"
                  onClick={addFaxField}
                  type="button"
                >
                  {BUTTONS.ADD_WITH_ICON}
                </button>
              </div>

              {/* Buttons */}
              <div className="h-[3.5rem] flex px-3 py-3 justify-center mt-auto border-t mb-3">
                <div className="flex h-8 gap-4.5">
                  <CustomButton
                    dataQaId="cancel-message-preview-data-with-link"
                    type="button"
                    color="primary"
                    widthClass="w-[7rem]"
                    className="text-xs"
                    isFilled={false}
                    onClick={() => {
                      setFaxSplitPanelVisible(false)
                      setIsSplitFax(false)
                    }}
                  >
                    <span className="text-xs font-light">{BUTTONS.CANCEL}</span>
                  </CustomButton>
                  <CustomButton
                    dataQaId="save-message-final-html"
                    type="button"
                    color="primary"
                    widthClass="w-[7rem]"
                    className="text-xs"
                    isFilled={true}
                    disabled={!isValid}
                    onClick={handleSplitSubmit(onSubmit)}
                  >
                    <span className="text-xs font-light">
                      {faxDetails?.category_communication_type?.label ===
                      QUEUE_STRINGS.MAIL
                        ? BUTTONS.SPLIT_MAIL
                        : BUTTONS.SPLIT_FAX}
                    </span>
                  </CustomButton>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  )
}

export default SplitFax
